import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import appointmentService from '../../../services/appointment.service';
import { showError, showSuccess } from '../../../utils/ecxSwal';
import SubHeader from '../../layout/components/SubHeader';
import Pagination from '../../common/Pagination';
import CancellationFollowupsSkeleton from './subparts/CancellationFollowupsSkeleton';
import {
    CANCELLATION_CHANNELS,
    buildCancellationSendJobs,
    buildDefaultChannelSelection,
    formatAppointmentDateTime,
    formatPatientName,
    getChannelState,
    getRowChannelSelection,
    getRowKey,
    getSendableRowKeys,
    isRowSendable,
    resolvePreferredChannel,
} from './subparts/cancellationFollowupChannel';
import { getAppointmentStatusMeta } from '../appointments/subparts/appointmentsQuery';
import {
    buildCancellationFollowupsPrintSubtitle,
    printCancellationFollowupsList,
} from './subparts/cancellationFollowupsPrint';
import './CancellationFollowups.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

function CancellationFollowups() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const setCancellationFollowupsCount = useEyeconxStore((state) => state.setCancellationFollowupsCount);

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [sending, setSending] = useState(false);
    const [channelSelections, setChannelSelections] = useState({});
    const [selectedRowKeys, setSelectedRowKeys] = useState(() => new Set());

    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const selectAllRef = useRef(null);

    const fetchRecords = useCallback(
        async (page, size, options = {}) => {
            const { silent = false } = options;

            if (!clinicId) {
                return;
            }

            if (!silent) {
                setLoading(true);
            }

            try {
                const res = await appointmentService.getCancellationFollowups(clinicId, page, size);
                const data = res.data;
                const content = data.content || [];

                setRecords(content);
                setPageNumber(data.number ?? page);
                setPageSize(data.size ?? size);
                setTotalPages(data.totalPages ?? 0);
                setTotalElements(data.totalElements ?? 0);
                setCancellationFollowupsCount(data.totalElements ?? 0);

                const nextSelections = {};
                content.forEach((record) => {
                    nextSelections[getRowKey(record)] = buildDefaultChannelSelection(record);
                });
                setChannelSelections(nextSelections);
                setSelectedRowKeys(new Set(getSendableRowKeys(content)));
            } catch (err) {
                console.error('Failed to load cancellation followups', err);
                toast.error('Failed to load cancellation followups');
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [clinicId, setCancellationFollowupsCount]
    );

    useEffect(() => {
        fetchRecords(pageNumber, pageSize);
    }, [fetchRecords, pageNumber, pageSize]);

    const sendableRowKeys = useMemo(() => getSendableRowKeys(records), [records]);
    const allSendableSelected =
        sendableRowKeys.length > 0 && sendableRowKeys.every((key) => selectedRowKeys.has(key));
    const someSendableSelected = sendableRowKeys.some((key) => selectedRowKeys.has(key));

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someSendableSelected && !allSendableSelected;
        }
    }, [allSendableSelected, someSendableSelected]);

    const selectedSendJobs = useMemo(
        () => buildCancellationSendJobs(records, selectedRowKeys, channelSelections),
        [records, selectedRowKeys, channelSelections]
    );

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await fetchRecords(pageNumber, pageSize, { silent: true });
        } finally {
            setIsRefreshing(false);
        }
    };

    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setPageNumber(0);
    };

    const handleChannelSelect = (rowKey, channelKey, disabled) => {
        if (disabled) {
            return;
        }

        setChannelSelections((prev) => ({
            ...prev,
            [rowKey]: channelKey,
        }));
    };

    const handleToggleSelectAll = () => {
        setSelectedRowKeys((prev) => {
            if (allSendableSelected) {
                return new Set();
            }

            return new Set(sendableRowKeys);
        });
    };

    const handleToggleRow = (rowKey, sendable) => {
        if (!sendable) {
            return;
        }

        setSelectedRowKeys((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }
            return next;
        });
    };

    const handleSendFollowups = async () => {
        if (!clinicId) {
            return;
        }

        const jobs = selectedSendJobs;

        if (!jobs.length) {
            toast.info('Select at least one patient with an available email or SMS channel.');
            return;
        }

        const emailCount = jobs.filter((job) => job.mediumType === 'EMAIL').length;
        const smsCount = jobs.filter((job) => job.mediumType === 'SMS').length;
        const channelSummary = [
            emailCount ? `${emailCount} email${emailCount === 1 ? '' : 's'}` : null,
            smsCount ? `${smsCount} SMS` : null,
        ]
            .filter(Boolean)
            .join(' and ');

        const result = await Swal.fire({
            title: 'Send cancellation follow-ups?',
            html: `Queue <strong>${channelSummary}</strong> for the selected patients.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Send',
            cancelButtonText: 'Cancel',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'btn btn-primary rounded-pill px-3 me-2',
                cancelButton: 'btn btn-outline-secondary rounded-pill px-3 ecx-swal__btn ecx-swal__btn--cancel',
            },
        });

        if (!result.isConfirmed) {
            return;
        }

        setSending(true);

        try {
            const response = await appointmentService.sendCancellationFollowups(
                clinicId,
                jobs.map((job) => ({
                    patientId: job.patientId,
                    appointmentId: job.appointmentId,
                    mediumType: job.mediumType,
                }))
            );

            const { queued = 0, failed = 0 } = response.data || {};

            if (queued > 0) {
                showSuccess({
                    title: 'Follow-ups Queued',
                    text:
                        failed > 0
                            ? `${queued} message${queued === 1 ? '' : 's'} queued. ${failed} failed.`
                            : `${queued} follow-up message${queued === 1 ? '' : 's'} queued for delivery.`,
                });
                await fetchRecords(pageNumber, pageSize, { silent: true });
            } else {
                showError({
                    title: 'Send Failed',
                    text: 'Could not queue follow-up messages for the selected patients.',
                });
            }
        } catch (error) {
            console.error('Failed to queue cancellation follow-ups', error);
            showError({
                title: 'Send Failed',
                text: 'Could not queue follow-up messages for the selected patients.',
            });
        } finally {
            setSending(false);
        }
    };

    const toolbarMeta = useMemo(
        () => ({
            pageLabel: `Page ${totalPages === 0 ? 0 : pageNumber + 1} of ${Math.max(totalPages, 1)}`,
            totalLabel: `${totalElements} patient${totalElements === 1 ? '' : 's'}`,
        }),
        [pageNumber, totalPages, totalElements]
    );

    const description = useMemo(
        () =>
            totalElements
                ? `Unconfirmed appointments from the last 7 days · ${totalElements} total record${totalElements === 1 ? '' : 's'}`
                : 'Review and send follow-up outreach for unconfirmed appointments',
        [totalElements]
    );

    const handlePrint = () => {
        const printed = printCancellationFollowupsList({
            records,
            channelSelections,
            title: 'Cancellation Followups',
            subtitle: buildCancellationFollowupsPrintSubtitle({
                recordsCount: records.length,
                totalElements,
                pageNumber,
                totalPages,
            }),
        });

        if (!printed) {
            toast.error('Unable to open print preview. Please try again.');
        }
    };

    return (
        <div className="ecx-page cancellation-followups-page ecx-page--full-width">
            <SubHeader
                title="Cancellation Followups"
                description={description}
                icon="bi-calendar-x"
                containerClass="w-100 px-4"
            >
                <button
                    type="button"
                    className="btn btn-light-primary btn-sm rounded-pill"
                    onClick={handleRefresh}
                    disabled={loading || isRefreshing || sending}
                    title="Refresh cancellation followups"
                >
                    <i className={`bi bi-arrow-clockwise me-1 ${isRefreshing ? 'ecx-refresh-spin' : ''}`}></i>
                    Refresh
                </button>
            </SubHeader>

            <div className="ecx-page-body">
                <div className="ecx-page-card ecx-page-panel cancellation-followups-panel">
                    <div className="ecx-page-panel__header">
                        <div className="cancellation-followups-toolbar">
                            <div className="cancellation-followups-toolbar__meta">
                                <h5 className="mb-0 fw-normal text-dark">Follow-up List</h5>
                                <span className="ecx-page-chip">
                                    <i className="bi bi-list-ul"></i>
                                    {toolbarMeta.totalLabel}
                                </span>
                                <span className="ecx-page-chip">
                                    <i className="bi bi-layers"></i>
                                    {toolbarMeta.pageLabel}
                                </span>
                            </div>

                            <div className="cancellation-followups-toolbar__controls">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary rounded-pill"
                                    onClick={handleSendFollowups}
                                    disabled={loading || sending || selectedSendJobs.length === 0}
                                    aria-busy={sending}
                                >
                                    {sending ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-1"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-send me-1" />
                                            Send Follow-up
                                            {selectedSendJobs.length > 0 ? ` (${selectedSendJobs.length})` : ''}
                                        </>
                                    )}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={handlePrint}
                                    disabled={loading || records.length === 0 || sending}
                                >
                                    <i className="bi bi-printer me-1"></i>
                                    Print
                                </button>

                                <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                                    Show
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: '5rem' }}
                                        value={pageSize}
                                        onChange={handlePageSizeChange}
                                        disabled={loading || sending}
                                    >
                                        {PAGE_SIZE_OPTIONS.map((size) => (
                                            <option key={size} value={size}>
                                                {size}
                                            </option>
                                        ))}
                                    </select>
                                    entries
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="ecx-page-panel__body d-flex flex-column p-0 position-relative">
                        {sending && (
                            <div className="cancellation-followups-sending-overlay" role="status" aria-live="polite">
                                <div className="cancellation-followups-sending-overlay__content">
                                    <span
                                        className="spinner-border text-primary cancellation-followups-sending-overlay__spinner"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                    <p className="cancellation-followups-sending-overlay__title mb-1">
                                        Sending follow-up messages
                                    </p>
                                    <p className="cancellation-followups-sending-overlay__subtitle mb-0">
                                        Queueing {selectedSendJobs.length} message
                                        {selectedSendJobs.length === 1 ? '' : 's'}...
                                    </p>
                                </div>
                            </div>
                        )}

                        {loading ? (
                            <CancellationFollowupsSkeleton count={pageSize > 8 ? 8 : pageSize} />
                        ) : records.length > 0 ? (
                            <>
                                <div className="table-responsive cancellation-followups-table-wrap">
                                    <table className="table table-hover align-middle mb-0 cancellation-followups-table">
                                        <thead>
                                            <tr>
                                                <th className="cancellation-followups-select-col ps-3">
                                                    <input
                                                        ref={selectAllRef}
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={allSendableSelected}
                                                        disabled={sendableRowKeys.length === 0 || loading || sending}
                                                        onChange={handleToggleSelectAll}
                                                        aria-label="Select all patients on this page"
                                                    />
                                                </th>
                                                <th>Patient</th>
                                                <th>Email</th>
                                                <th>Cell Phone</th>
                                                <th>Home Phone</th>
                                                <th>Missed Appointment</th>
                                                <th>Status</th>
                                                {CANCELLATION_CHANNELS.map((channel) => (
                                                    <th key={channel.key} className="text-center">
                                                        {channel.label}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {records.map((record) => {
                                                const rowKey = getRowKey(record);
                                                const patientName = formatPatientName(record);
                                                const preferredChannel = resolvePreferredChannel(record);
                                                const selectedChannel = getRowChannelSelection(
                                                    channelSelections,
                                                    rowKey,
                                                    record
                                                );
                                                const sendable = isRowSendable(record);
                                                const selected = selectedRowKeys.has(rowKey);
                                                const statusMeta = getAppointmentStatusMeta(record.isConfirmed);

                                                return (
                                                    <tr key={rowKey} className={!sendable ? 'is-disabled-row' : ''}>
                                                        <td className="cancellation-followups-select-col ps-3">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={selected}
                                                                disabled={!sendable || loading || sending}
                                                                onChange={() => handleToggleRow(rowKey, sendable)}
                                                                aria-label={`Select ${patientName}`}
                                                            />
                                                        </td>
                                                        <td className="cancellation-followups-patient">
                                                            <Link
                                                                to={`/patient-files/${record.id}`}
                                                                className="cancellation-followups-patient-link fw-normal"
                                                            >
                                                                {patientName}
                                                            </Link>
                                                        </td>
                                                        <td className="cancellation-followups-contact">
                                                            {record.email || <span className="text-muted">—</span>}
                                                        </td>
                                                        <td className="cancellation-followups-contact">
                                                            {record.cellPhone || <span className="text-muted">—</span>}
                                                        </td>
                                                        <td className="cancellation-followups-contact">
                                                            {record.homePhone || <span className="text-muted">—</span>}
                                                        </td>
                                                        <td className="cancellation-followups-appointment">
                                                            {formatAppointmentDateTime(record)}
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={`cancellation-followups-status cancellation-followups-status--${statusMeta.variant}`}
                                                            >
                                                                <i className={`bi ${statusMeta.icon}`} />
                                                                {statusMeta.label}
                                                            </span>
                                                        </td>
                                                        {CANCELLATION_CHANNELS.map((channel) => {
                                                            const state = getChannelState(record, channel.key);
                                                            const isPreferred = preferredChannel === channel.key;
                                                            const checked = selectedChannel === channel.key;
                                                            const title = state.disabled
                                                                  ? !state.available
                                                                      ? `No ${channel.label.toLowerCase()} contact on file`
                                                                      : `${channel.label} disabled in communication preferences`
                                                                  : isPreferred
                                                                    ? `Preferred ${channel.label.toLowerCase()} channel`
                                                                    : channel.label;

                                                            return (
                                                                <td key={`${rowKey}-${channel.key}`} className="text-center">
                                                                    <div
                                                                        className={`cancellation-followups-channel ${
                                                                            isPreferred ? 'is-preferred' : ''
                                                                        } ${state.disabled ? 'is-disabled' : ''}`}
                                                                        title={title}
                                                                    >
                                                                        <input
                                                                            type="radio"
                                                                            className="form-check-input"
                                                                            name={`cancellation-channel-${rowKey}`}
                                                                            checked={checked}
                                                                            disabled={state.disabled || loading || sending}
                                                                            onChange={() =>
                                                                                handleChannelSelect(
                                                                                    rowKey,
                                                                                    channel.key,
                                                                                    state.disabled
                                                                                )
                                                                            }
                                                                            aria-label={`${channel.label} for ${patientName}`}
                                                                        />
                                                                    </div>
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {totalPages > 1 && (
                                    <div className="ecx-page-pagination border-top p-3 mt-auto">
                                        <Pagination
                                            currentPage={pageNumber}
                                            totalPages={totalPages}
                                            totalElements={totalElements}
                                            pageSize={pageSize}
                                            onPageChange={setPageNumber}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="ecx-page-empty flex-grow-1">
                                <div className="ecx-page-empty__icon">
                                    <i className="bi bi-calendar-check"></i>
                                </div>
                                <h5 className="text-dark fw-normal mb-2">No follow-ups needed</h5>
                                <p className="mb-0">
                                    There are no unconfirmed appointments from the last 7 days.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CancellationFollowups;
